import { configureStore, isPlain } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import thunk from 'redux-thunk';

import alertsSlice from './alerts/alertsSlice';
import recommendationsSlice from './alerts/recommendationsSlice';
import rewardActivitiesSlice from './alerts/rewardActivitiesSlice';
import authSlice from './auth/authSlice';
import devicesSlice from './devices/devicesSlice';
import familySlice from './family/familySlice';
import identityMonitoringSlice from './identityMonitoring/identityMonitoringSlice';
import layoutSlice from './layout/layoutSlice';
import notificationsSlice from './notifications/notificationsSlice';
import performanceSlice from './performance/performanceSlice';
import userProfileSlice from './userProfile/userProfileSlice';
import webViewSlice from './webView/webViewSlice';

export default function configureAppStore(preloadedState?: object) {
  const rootReducer = combineReducers({
    alerts: alertsSlice,
    auth: authSlice,
    devices: devicesSlice,
    notifications: notificationsSlice,
    identityMonitoring: identityMonitoringSlice,
    recommendations: recommendationsSlice,
    rewardActivities: rewardActivitiesSlice,
    layout: layoutSlice,
    performance: performanceSlice,
    webView: webViewSlice,
    userProfile: userProfileSlice,
    family: familySlice,
  });

  const enhancers = [];
  const windowIfDefined = typeof window === 'undefined' ? null : (window as any); // eslint-disable-line @typescript-eslint/no-explicit-any

  if (windowIfDefined && windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__) {
    enhancers.push(windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__());
  }

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Configure SerializableStateInvariantMiddleware to not warn about Dates
          isSerializable: (value: unknown) =>
            isPlain(value) ||
            /**
             * Generally, we should avoid using mutable or non-serializable objects in Redux state.
             * See
             *  - https://github.com/reduxjs/redux-toolkit/issues/456
             *  - https://redux.js.org/faq/organizing-state#can-i-put-functions-promises-or-other-non-serializable-items-in-my-store-state
             *
             * Allowing Date here means we accept that
             *  - we must not mutate the Date objects in state, and
             *  - the potential drawbacks of
             *    > things like persistence and time-travel debugging potentially not working as intended
             *
             * Considering there are a lot of timestamps in the API responses
             * (which are converted to Date by AutoRest (https://github.com/Azure/autorest.typescript/issues/755)),
             * allowing Date here is a pragmatic choice.
             */
            value instanceof Date,
        },
      }).concat(thunk),
    preloadedState,
  });

  return store;
}
